import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, AlertTriangle, ArrowLeft, FileMinus } from 'lucide-react';
import { toast } from 'sonner';
import { correctionApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errorUtils';

const eur = (v) => (v ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const WARN_LABELS = {
  SUPPLIER_ORDER_PRESENT: "Esiste un ordine collegato al fornitore. Lo storno lato cliente non annulla automaticamente l'impegno verso il fornitore.",
  SUPPLIER_ACCOUNTING_COST_PRESENT: 'Esistono costi/fatture/pagamenti fornitore che resteranno contabilizzati.',
  GOODS_READY_FLAG: 'Il materiale risulta segnalato come pronto/consegnato.',
};
const BLOCKER_LABELS = {
  BLOCKED_NOT_ELIGIBLE: "Questo Extra non è stornabile (non è un accordo firmato/effective). Usa l'editor o l'eliminazione normale.",
  BLOCKED_BY_EXECUTION: 'La lavorazione risulta già eseguita: è possibile stornare solo il residuo disponibile.',
  BLOCKED_NOTHING_TO_STORNO: 'Non c\'è valore residuo da stornare.',
  BLOCKED_INVALID_PAYLOAD: 'Selezione non valida.',
};

export const ExtraStornoDialog = ({ open, onOpenChange, extra, onCreated }) => {
  const cs = extra?.corrections_summary || {};
  const [step, setStep] = useState('type'); // type | config | preview
  const [loadingPf, setLoadingPf] = useState(false);
  const [basePf, setBasePf] = useState(null); // GET full preflight (per_item, eligibility)
  const [correctionType, setCorrectionType] = useState('full');
  const [qty, setQty] = useState({}); // {code: number}
  const [effect, setEffect] = useState(null); // active preflight result for chosen mode
  const [evaluating, setEvaluating] = useState(false);
  const [reason, setReason] = useState('');
  const [creating, setCreating] = useState(false);

  const resetAll = useCallback(() => {
    setStep('type'); setBasePf(null); setCorrectionType('full'); setQty({});
    setEffect(null); setReason(''); setCreating(false); setEvaluating(false);
  }, []);

  useEffect(() => {
    if (!open || !extra?.id) return;
    resetAll();
    setLoadingPf(true);
    correctionApi.preflight(extra.id, 'full')
      .then((r) => setBasePf(r.data))
      .catch((e) => toast.error(getErrorMessage(e, 'Errore preflight storno')))
      .finally(() => setLoadingPf(false));
  }, [open, extra?.id, resetAll]);

  const notEligible = basePf && basePf.classification === 'BLOCKED_NOT_ELIGIBLE';
  const perItem = basePf?.per_item || [];

  const evaluatePartial = useCallback(async () => {
    const items = Object.entries(qty)
      .filter(([, q]) => Number(q) > 0)
      .map(([code, q]) => ({ original_item_code: code, quantity_storno: Number(q) }));
    if (items.length === 0) { setEffect(null); return; }
    setEvaluating(true);
    try {
      const r = await correctionApi.preflightPost(extra.id, { correction_type: 'partial', items });
      setEffect(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore nel ricalcolo'));
    } finally { setEvaluating(false); }
  }, [qty, extra?.id]);

  const chooseType = (t) => {
    setCorrectionType(t);
    if (t === 'full') { setEffect(basePf); setStep('config'); }
    else { setEffect(null); setQty({}); setStep('config'); }
  };

  const qtyError = (it) => {
    const v = Number(qty[it.original_item_code] || 0);
    if (v < 0) return true;
    if (v > (it.residual_stornabile || 0) + 1e-6) return true;
    return false;
  };
  const anyQtyInvalid = perItem.some(qtyError);
  const hasAnyQty = Object.values(qty).some((q) => Number(q) > 0);

  const canProceed = () => {
    if (correctionType === 'full') return effect && effect.is_safe && effect.net_delta < 0;
    return effect && effect.is_safe && hasAnyQty && !anyQtyInvalid;
  };
  const reasonValid = reason.trim().length >= 8;

  const doCreate = async () => {
    setCreating(true);
    try {
      const payload = correctionType === 'full'
        ? { correction_type: 'full', reason: reason.trim() }
        : { correction_type: 'partial', reason: reason.trim(),
            items: Object.entries(qty).filter(([, q]) => Number(q) > 0)
              .map(([code, q]) => ({ original_item_code: code, quantity_storno: Number(q) })) };
      await correctionApi.create(extra.id, payload);
      toast.success('Rettifica creata in Bozza');
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore nella creazione della rettifica'));
    } finally { setCreating(false); }
  };

  const warnings = effect?.warnings || basePf?.warnings || [];
  const fiscalHold = effect?.fiscal_hold_required || basePf?.fiscal_hold_required;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col" data-testid="extra-storno-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#171717]">
            <FileMinus className="w-5 h-5 text-red-600" /> Storna / rettifica Extra
          </DialogTitle>
        </DialogHeader>

        {loadingPf ? (
          <div className="py-10 flex items-center justify-center text-[#737373]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifica in corso…</div>
        ) : notEligible ? (
          <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="storno-not-eligible">{BLOCKER_LABELS.BLOCKED_NOT_ELIGIBLE}</div>
        ) : (
          <div className="overflow-y-auto pr-1 space-y-4 flex-1">
            {/* Riepilogo Extra */}
            <div className="rounded-lg border border-[#ECECEC] p-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{extra.extra_number}</span>
                <span className="text-[#737373]">{extra.title}</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{extra.extra_type === 'fornitura' ? 'Fornitura' : 'Lavoro'}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                <div><div className="text-[#737373]">Valore originario</div><div className="font-semibold tabular-nums">{eur(cs.original_net)}</div></div>
                <div><div className="text-[#737373]">Rettifiche effettive</div><div className="font-semibold text-red-600 tabular-nums">{eur(cs.corrections_net_effective)}</div></div>
                <div><div className="text-[#737373]">Valore corrente</div><div className="font-bold tabular-nums">{eur(cs.current_net)}</div></div>
              </div>
            </div>

            {/* STEP: scelta tipo */}
            {step === 'type' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button type="button" data-testid="storno-choose-full" onClick={() => chooseType('full')}
                  className="text-left rounded-lg border border-[#ECECEC] hover:border-red-300 hover:bg-red-50/40 p-4 transition-colors">
                  <div className="font-semibold text-[#171717]">Storno totale</div>
                  <div className="text-xs text-[#737373] mt-1">Storna tutto il valore residuo dell'Extra.</div>
                </button>
                <button type="button" data-testid="storno-choose-partial" onClick={() => chooseType('partial')}
                  className="text-left rounded-lg border border-[#ECECEC] hover:border-orange-300 hover:bg-orange-50/40 p-4 transition-colors">
                  <div className="font-semibold text-[#171717]">Storno parziale</div>
                  <div className="text-xs text-[#737373] mt-1">Riduce solo alcune voci o quantità.</div>
                </button>
              </div>
            )}

            {/* STEP: config */}
            {step === 'config' && correctionType === 'full' && (
              <div className="rounded-lg border border-[#ECECEC] p-3 space-y-1 text-sm" data-testid="storno-full-config">
                {!effect?.is_safe ? (
                  <div className="text-red-700">{BLOCKER_LABELS[effect?.classification] || 'Storno totale non disponibile.'}</div>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-[#737373]">Valore corrente</span><span className="tabular-nums">{eur(cs.current_net)}</span></div>
                    <div className="flex justify-between text-red-600 font-medium"><span>Storno (netto)</span><span className="tabular-nums" data-testid="storno-full-net">{eur(effect.net_delta)}</span></div>
                    <div className="flex justify-between"><span className="text-[#737373]">IVA</span><span className="tabular-nums">{eur(effect.vat_delta)}</span></div>
                    <div className="flex justify-between"><span className="text-[#737373]">Storno (lordo)</span><span className="tabular-nums">{eur(effect.gross_delta)}</span></div>
                    <div className="flex justify-between border-t border-dashed border-[#ECECEC] pt-1 mt-1 font-semibold"><span>Nuovo valore previsto</span><span className="tabular-nums">{eur((cs.current_net || 0) + effect.net_delta)}</span></div>
                  </>
                )}
              </div>
            )}

            {step === 'config' && correctionType === 'partial' && (
              <div className="space-y-2" data-testid="storno-partial-config">
                <div className="max-h-[32vh] overflow-y-auto rounded-lg border border-[#ECECEC] divide-y">
                  {perItem.map((it) => (
                    <div key={it.original_item_code} className="p-2 text-xs" data-testid={`storno-item-${it.original_item_code}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-[#171717] truncate">{it.original_item_code} · {it.description}</div>
                          <div className="text-[#8a8a8a] mt-0.5">
                            Orig {it.original_quantity} {it.unit} · Eseguito {it.executed_quantity} · Già stornato {it.already_storned_quantity} · <span className="text-emerald-700">Max {it.residual_stornabile} {it.unit}</span> · {eur(it.unit_price)}/{it.unit} · IVA {it.vat_percentage}%
                          </div>
                        </div>
                        <div className="shrink-0 w-28">
                          <Input type="number" min="0" step="0.01" max={it.residual_stornabile}
                            data-testid={`storno-qty-${it.original_item_code}`}
                            value={qty[it.original_item_code] ?? ''}
                            onChange={(e) => setQty((p) => ({ ...p, [it.original_item_code]: e.target.value }))}
                            onBlur={evaluatePartial}
                            className={qtyError(it) ? 'border-red-500' : ''}
                            placeholder="0" disabled={it.residual_stornabile <= 0} />
                          {qtyError(it) && <div className="text-[10px] text-red-600 mt-0.5" data-testid={`storno-qty-error-${it.original_item_code}`}>Max {it.residual_stornabile}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-[#ECECEC] p-3 text-sm flex items-center justify-between">
                  <span className="text-[#737373] flex items-center gap-1">{evaluating && <Loader2 className="w-3 h-3 animate-spin" />} Storno netto</span>
                  <span className="font-semibold text-red-600 tabular-nums" data-testid="storno-partial-net">{effect && effect.net_delta < 0 ? eur(effect.net_delta) : eur(0)}</span>
                </div>
              </div>
            )}

            {/* Warnings */}
            {step !== 'type' && warnings.filter((w) => w !== 'FISCAL_HOLD_REQUIRED').map((w) => (
              <div key={w} className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2" data-testid={`storno-warning-${w}`}>
                <AlertTriangle className="w-4 h-4 shrink-0" /> <span>{WARN_LABELS[w] || w}</span>
              </div>
            ))}
            {step !== 'type' && fiscalHold && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700 flex gap-2" data-testid="storno-fiscal-hold">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span><strong>Regolarizzazione fiscale necessaria.</strong> La rettifica può essere predisposta, ma non potrà produrre effetti economici finché la posizione fiscale non verrà regolarizzata.</span>
              </div>
            )}

            {/* Motivo + preview */}
            {step === 'config' && (
              <div className="space-y-1">
                <Label className="text-xs text-[#737373]">Motivo della rettifica *</Label>
                <Textarea data-testid="storno-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  placeholder="Il cliente rinuncia alla fornitura e sceglie un prodotto differente." />
                {!reasonValid && reason.length > 0 && <div className="text-[10px] text-red-600">Minimo 8 caratteri.</div>}
              </div>
            )}

            {step === 'preview' && effect && (
              <div className="rounded-lg border border-[#ECECEC] p-3 text-sm space-y-1" data-testid="storno-preview">
                <div className="font-semibold text-[#171717] mb-1">Riepilogo rettifica</div>
                <div className="flex justify-between"><span className="text-[#737373]">Tipo</span><span>{correctionType === 'full' ? 'Totale' : 'Parziale'}</span></div>
                <div className="flex justify-between"><span className="text-[#737373]">Valore corrente</span><span className="tabular-nums">{eur(cs.current_net)}</span></div>
                <div className="flex justify-between text-red-600 font-medium"><span>Rettifica (netto)</span><span className="tabular-nums">{eur(effect.net_delta)}</span></div>
                <div className="flex justify-between border-t border-dashed border-[#ECECEC] pt-1 font-semibold"><span>Valore residuo previsto</span><span className="tabular-nums">{eur((cs.current_net || 0) + effect.net_delta)}</span></div>
                <div className="text-xs text-[#737373] pt-1">Motivo: {reason.trim()}</div>
                <div className="text-[11px] text-[#8a8a8a] pt-2 border-t border-[#ECECEC] mt-1">La rettifica verrà creata in <strong>Bozza</strong>. Non produrrà ancora effetti economici.</div>
              </div>
            )}
          </div>
        )}

        {!loadingPf && !notEligible && (
          <DialogFooter className="flex items-center justify-between gap-2 border-t pt-3">
            <div>
              {step !== 'type' && (
                <Button variant="ghost" size="sm" onClick={() => setStep(step === 'preview' ? 'config' : 'type')} data-testid="storno-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="storno-cancel">Annulla</Button>
              {step === 'config' && (
                <Button onClick={() => setStep('preview')} disabled={!canProceed() || !reasonValid} data-testid="storno-continue">Continua</Button>
              )}
              {step === 'preview' && (
                <Button onClick={doCreate} disabled={creating} className="bg-red-600 hover:bg-red-700 text-white" data-testid="storno-create">
                  {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Crea rettifica
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExtraStornoDialog;
